import React from 'react';
import { useCountryCities } from '../../../utils/useCountryCities';
import { isUnitedKingdomCountry, isUnitedStatesCountry } from '../../../utils/investorPricingView';
import { allUkCities, citiesForUkNation, isUkNation } from '../../../constants/ukLocations';

/** Closed select surface — same white fill as text inputs. */
export const PROFILE_SELECT_SURFACE = 'bg-white text-gray-900';

/** Shared Mac-style native select chrome (transparent system menu, custom chevron). */
export const NATIVE_SELECT_CHEVRON_STYLE: React.CSSProperties = {
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  backgroundSize: '16px 16px',
  paddingRight: '40px',
};

type CitySelectProps = {
  id?: string;
  name?: string;
  country: string;
  /** UK county/nation or US state — required before cities load for UK/US. */
  region?: string;
  value: string;
  onChange: (city: string) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  error?: boolean;
};

/**
 * City dropdown for any country. UK/US wait for county/state, then load cities.
 */
const CitySelect: React.FC<CitySelectProps> = ({
  id = 'city',
  name = 'city',
  country,
  region = '',
  value,
  onChange,
  required,
  disabled,
  className = '',
  error,
}) => {
  const isUk = isUnitedKingdomCountry(country);
  const isUs = isUnitedStatesCountry(country);
  const regionTrimmed = region.trim();
  const { cities: remoteCities, loading } = useCountryCities(
    isUk ? '' : country,
    isUs ? regionTrimmed : undefined
  );

  const ukCities = isUk
    ? isUkNation(regionTrimmed)
      ? [...citiesForUkNation(regionTrimmed)]
      : regionTrimmed
        ? allUkCities()
        : []
    : [];
  const baseCities = isUk ? ukCities : remoteCities;
  const options =
    value.trim() && !baseCities.includes(value.trim()) ? [value.trim(), ...baseCities] : baseCities;

  const waitingOnRegion = (isUk || isUs) && !regionTrimmed;
  const showLoading = !isUk && !waitingOnRegion && loading && options.length === 0;
  const selectDisabled = Boolean(disabled || waitingOnRegion || showLoading);

  return (
    <select
      id={id}
      name={name}
      value={value}
      required={required}
      disabled={selectDisabled}
      onChange={e => onChange(e.target.value)}
      className={`${className} appearance-none disabled:opacity-60 ${
        error ? 'border-red-500' : ''
      }`}
      style={NATIVE_SELECT_CHEVRON_STYLE}
    >
      <option value="">
        {waitingOnRegion
          ? isUs
            ? 'Select state first'
            : 'Select county first'
          : showLoading
            ? 'Loading cities…'
            : 'Select city'}
      </option>
      {options.map(city => (
        <option key={city} value={city}>
          {city}
        </option>
      ))}
    </select>
  );
};

export default CitySelect;
